import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
	cloneElement,
	type HTMLAttributes,
	type ReactElement,
	type RefObject,
	useRef,
	useState
} from 'react'
import { Controller, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { z } from 'zod'
import { InputText } from '@/components/commons/input-text'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogTitle,
	DialogTrigger
} from '@/components/ui/dialog'
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger
} from '@/components/ui/drawer'
import { Label } from '@/components/ui/label'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { RequiredLabel } from '@/features/marquee/components/event-form-atoms'
import { useMinWidth } from '@/features/marquee/hooks/use-min-width'
import { makeQueryKeys } from '@/features/marquee/query-keys'
import {
	MARQUEE_RSVP_ENTRY_OPTIONS,
	MARQUEE_SEGMENT_OPTIONS,
	type MarqueeSegment
} from '@/features/marquee/types'
import {
	isAttendeeEmailOnRoster,
	rsvpEntryToLabelKey,
	segmentLabel
} from '@/features/marquee/utils'
import { errorHandler } from '@/helpers/utils'
import { addAttendee } from '@/sdk'
import { type TKey, useTranslation } from '@/translations/i18n'

/** One row of the invitee CSV: the nine columns `COLUMN_ALIASES` reads. */
const AttendeeSchema = z.object({
	name: z.string().trim().min(1, 'marquee:error_attendee_name_required'),
	company: z.string().trim().optional(),
	email: z.string().trim().optional(),
	accountName: z.string().trim().optional(),
	airbnbHostId: z.string().trim().optional(),
	hostId18: z.string().trim().optional(),
	segment: z.enum(MARQUEE_SEGMENT_OPTIONS).nullable().optional(),
	language: z.string().trim().optional(),
	rsvpStatus: z.enum(MARQUEE_RSVP_ENTRY_OPTIONS).nullable().optional()
})

type AttendeeValues = z.infer<typeof AttendeeSchema>
type RsvpEntry = (typeof MARQUEE_RSVP_ENTRY_OPTIONS)[number]

const EMPTY: AttendeeValues = {
	name: '',
	company: '',
	email: '',
	accountName: '',
	airbnbHostId: '',
	hostId18: '',
	segment: null,
	language: '',
	// The invite has gone out and nothing has come back, which is what a late
	// addition is by definition and what the file's `Sent` column means.
	rsvpStatus: 'invited'
}

interface AddAttendeeDialogProps {
	eventId?: string
	trigger: ReactElement<HTMLAttributes<HTMLElement>>
}

/**
 * "Add attendee" form: the single late invitee an MM would otherwise have to
 * add by editing the spreadsheet and re-uploading the whole file.
 *
 * The form is one row of the invitee file, column for column, so a typed row and
 * an imported one land identically: the backend reads this payload through the
 * same derivation the importer uses (`account_name` outranks `company` and its
 * `(Parent)` suffix plus a clean `001…` id produce `sf_parent_account_id`, and
 * `rsvp` is read in the file's own vocabulary).
 *
 * Deliberately not the walk-in write. A walk-in is created already present at
 * the desk, while this is a `pre_work` invitee who has not arrived:
 * `POST /events/{id}/attendees` stores the row with `origin = manual` and runs
 * the same resolve pass an imported row takes.
 *
 * `name` is optional in the contract, because the importer would rather store a
 * visibly incomplete row than fail a 200-row upload. It stays required here for
 * the reason the endpoint gives for answering 409 on a duplicate email instead
 * of dropping it: a single interactive add has someone to ask.
 *
 * Desktop → `Dialog`, mobile → `Drawer`; the form body is shared.
 */
function AddAttendeeDialog({ eventId, trigger }: AddAttendeeDialogProps) {
	const { t } = useTranslation()
	const isDesktop = useMinWidth('l')
	// Portal targets for the option lists, one per surface (only one is mounted).
	const dialogContentRef = useRef<HTMLDivElement>(null)
	const drawerContentRef = useRef<HTMLDivElement>(null)
	const [open, setOpen] = useState(false)

	const {
		register,
		control,
		handleSubmit,
		reset,
		setError,
		formState: { errors }
	} = useForm<AttendeeValues>({
		resolver: zodResolver(AttendeeSchema),
		defaultValues: EMPTY
	})

	const close = () => {
		setOpen(false)
		reset(EMPTY)
	}

	const queryClient = useQueryClient()

	const add = useMutation({
		mutationFn: async (values: AttendeeValues) => {
			// One invitee-file row, column for column. Blanks go as `undefined`
			// rather than `''`: the backend collapses empty strings to null
			// anyway, and an empty account name would otherwise count as its
			// own value in the shared-host rule.
			const response = await addAttendee({
				path: { eventId: eventId ?? '' },
				body: {
					name: values.name.trim(),
					company: values.company?.trim() || undefined,
					email: values.email?.trim() || undefined,
					account_name: values.accountName?.trim() || undefined,
					airbnb_host_id: values.airbnbHostId?.trim() || undefined,
					host_id_18: values.hostId18?.trim() || undefined,
					segment: values.segment ?? undefined,
					language: values.language?.trim() || undefined,
					rsvp: values.rsvpStatus ?? undefined
				}
			})
			return response.data
		},
		onSuccess: async (attendee) => {
			await queryClient.invalidateQueries({
				queryKey: makeQueryKeys.marquee.events.detail(eventId ?? '')
			})
			toast.success(t('marquee:attendee_added', { name: attendee.name }))
			close()
		},
		onError: (error) => {
			// The duplicate is something the operator can correct by typing a
			// different address, so it lands on the field rather than in a toast.
			if (isAttendeeEmailOnRoster(error)) {
				setError('email', {
					type: 'server',
					message: 'marquee:error_attendee_email_on_roster'
				})
				return
			}
			errorHandler(error)
		}
	})

	const onSubmit = handleSubmit((values) => {
		if (!eventId) {
			return
		}
		add.mutate(values)
	})

	const errorText = (key?: string) => (key ? t(key as TKey) : undefined)

	// In CSV column order, so the form reads like the row it stands in for. Nine
	// fields is too tall for one column on desktop, so they pair up from `l` up;
	// the drawer only ever renders below that breakpoint and stays single-column.
	// The option list portals into the surrounding modal rather than the body:
	// both the drawer and the dialog disable pointer events outside their own
	// subtree, so a list left on the body cannot be tapped or scrolled, and
	// touching it reads as an interaction with the background.
	const fields = (container: RefObject<HTMLElement | null>) => (
		<div className='grid l:grid-cols-2 gap-5'>
			<InputText
				className='l:col-span-2'
				errorMessage={errorText(errors.name?.message)}
				label={
					<RequiredLabel
						label={t('marquee:checkin_walk_in_name_label')}
					/>
				}
				placeholder={t('marquee:checkin_walk_in_name_placeholder')}
				type='text'
				{...register('name')}
			/>
			<InputText
				label={t('marquee:add_attendee_company_label')}
				placeholder={t('marquee:add_attendee_company_placeholder')}
				type='text'
				{...register('company')}
			/>
			<InputText
				errorMessage={errorText(errors.email?.message)}
				label={t('marquee:add_attendee_email_label')}
				placeholder={t('marquee:checkin_walk_in_email_placeholder')}
				type='email'
				{...register('email')}
			/>
			<InputText
				label={t('marquee:add_attendee_account_name_label')}
				placeholder={t('marquee:add_attendee_account_name_placeholder')}
				type='text'
				{...register('accountName')}
			/>
			<InputText
				label={t('marquee:add_attendee_host_id_label')}
				placeholder={t('marquee:checkin_walk_in_host_id_placeholder')}
				type='text'
				{...register('airbnbHostId')}
			/>
			<InputText
				label={t('marquee:add_attendee_host_id_18_label')}
				placeholder={t('marquee:add_attendee_host_id_18_placeholder')}
				type='text'
				{...register('hostId18')}
			/>
			<Controller
				control={control}
				name='segment'
				render={({ field }) => (
					<div className='flex flex-col gap-1.5'>
						<Label htmlFor='segment'>
							{t('marquee:add_attendee_segment_label')}
						</Label>
						<Select
							onValueChange={field.onChange}
							value={field.value ?? null}
						>
							<SelectTrigger
								className='w-full'
								id='segment'
								nativeButton={false}
							>
								<SelectValue>
									{(value: MarqueeSegment | null) =>
										value
											? segmentLabel(value)
											: t(
													'marquee:add_attendee_segment_placeholder'
												)
									}
								</SelectValue>
							</SelectTrigger>
							<SelectContent
								className='min-w-(--anchor-width)'
								container={container}
							>
								{MARQUEE_SEGMENT_OPTIONS.map((option) => (
									<SelectItem key={option} value={option}>
										{segmentLabel(option)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				)}
			/>
			<InputText
				label={t('marquee:add_attendee_language_label')}
				placeholder={t('marquee:add_attendee_language_placeholder')}
				type='text'
				{...register('language')}
			/>
			<Controller
				control={control}
				name='rsvpStatus'
				render={({ field }) => (
					<div className='flex flex-col gap-1.5'>
						<Label htmlFor='rsvpStatus'>
							{t('marquee:add_attendee_rsvp_label')}
						</Label>
						<Select
							onValueChange={field.onChange}
							value={field.value ?? null}
						>
							<SelectTrigger
								className='w-full'
								id='rsvpStatus'
								nativeButton={false}
							>
								<SelectValue>
									{(value: RsvpEntry | null) =>
										value
											? t(rsvpEntryToLabelKey[value])
											: t(
													'marquee:add_attendee_rsvp_placeholder'
												)
									}
								</SelectValue>
							</SelectTrigger>
							<SelectContent
								className='min-w-(--anchor-width)'
								container={container}
							>
								{MARQUEE_RSVP_ENTRY_OPTIONS.map((option) => (
									<SelectItem key={option} value={option}>
										{t(rsvpEntryToLabelKey[option])}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				)}
			/>
		</div>
	)

	const actions = (
		<>
			<Button onClick={close} type='button' variant='tertiary'>
				{t('marquee:checkin_walk_in_back')}
			</Button>
			<Button
				disabled={add.isPending}
				onClick={onSubmit}
				type='button'
				variant='primary'
			>
				{t('marquee:add_attendee')}
				{add.isPending && <Spinner size={16} />}
			</Button>
		</>
	)

	if (isDesktop) {
		return (
			<Dialog
				onOpenChange={(next) => (next ? setOpen(true) : close())}
				open={open}
			>
				<DialogTrigger render={trigger} />
				{/* The body scrolls and the footer stays put: `DialogContent` is a
				    grid capped at the viewport, so its scrolling row needs an
				    explicit `minmax(0,1fr)` or it sizes to content and the
				    overflow-auto on `DialogBody` never engages. */}
				<DialogContent
					className='max-w-160 grid-rows-[minmax(0,1fr)_auto]'
					ref={dialogContentRef}
				>
					<DialogBody className='gap-4'>
						<div className='flex flex-col gap-1'>
							<DialogTitle>
								{t('marquee:add_attendee_title')}
							</DialogTitle>
							<DialogDescription className='text-neutral-500'>
								{t('marquee:add_attendee_subtitle')}
							</DialogDescription>
						</div>
						{fields(dialogContentRef)}
					</DialogBody>
					<DialogFooter className='flex flex-row justify-between'>
						{actions}
					</DialogFooter>
				</DialogContent>
			</Dialog>
		)
	}

	return (
		<Drawer
			onOpenChange={(next) => (next ? setOpen(true) : close())}
			open={open}
		>
			<DrawerTrigger asChild>
				{cloneElement(trigger, {
					onPointerDown: (e: React.PointerEvent) => e.preventDefault()
				})}
			</DrawerTrigger>
			<DrawerContent ref={drawerContentRef}>
				<DrawerHeader className='pb-4'>
					<DrawerTitle className='font-semibold text-neutral-900 text-xl'>
						{t('marquee:add_attendee_title')}
					</DrawerTitle>
					<DrawerDescription className='text-neutral-500'>
						{t('marquee:add_attendee_subtitle')}
					</DrawerDescription>
				</DrawerHeader>
				{/* Same reason, flex side: the drawer is capped at 80vh, so the
				    field list takes the leftover space and scrolls inside it
				    rather than pushing the footer off-screen. */}
				<div className='min-h-0 flex-1 overflow-y-auto px-4 pb-1'>
					{fields(drawerContentRef)}
				</div>
				<DrawerFooter className='flex-row justify-between'>
					{actions}
				</DrawerFooter>
			</DrawerContent>
		</Drawer>
	)
}

export { AddAttendeeDialog }
